import { useCreateChannelsTypingByCid } from "@birdwatch/client";
import { debounce } from "lodash-es";
import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  resolveTypingTransition,
  type TypingActivityType,
} from "../_libs/typing-transition";

/**
 * A repeat "typing" goes out at most this often. The receiver keeps a typist for
 * 8s (`TYPING_TTL_MS` in `ChannelTyping`), so a refresh every 3s keeps the
 * indicator up, and the route's per-IP budget, shared by an office's staff, is
 * not spent on every pause.
 */
const TYPING_REANNOUNCE_MS = 3_000;

interface UseTypingStatusOptions {
  /** When false the hook is inert: no debounce, no requests, `stopTyping` is a no-op. */
  enabled?: boolean;
  value: string;
  /** The conversation the composer posts to. */
  cid: string;
  debounceDelay?: number; // default 500ms
}

/**
 * Announces typing for one composer through `POST /channels/:cid/typing`.
 *
 * The invariant: announcements follow value *changes*, never the current value. The
 * mount value is the baseline, so restoring a draft, remounting after a refetch, or any
 * plain re-render announces nothing — only text the composer did not already hold
 * counts as typing.
 */
export function useTypingStatus({
  enabled = true,
  value,
  cid,
  debounceDelay = 500,
}: UseTypingStatusOptions) {
  // No refresh on success: the route's `typing.changed` is rendered from the event
  // itself (`ChannelTyping`), so there is nothing to refetch.
  const { mutate: announceTyping } = useCreateChannelsTypingByCid();

  /**
   * Whether this composer has an outstanding "typing" the peers are still showing.
   * Without it, every composer mount/unmount would announce a pointless stop.
   */
  const isTypingRef = useRef(false);

  /**
   * The text this composer last observed, seeded with the mount value — which is
   * already the restored draft. A remount re-presents the same string, so the
   * transition below resolves to "announce nothing".
   */
  const lastValueRef = useRef(value);

  /** When the last "typing" went out (epoch ms), for the re-announce throttle. */
  const lastStartAtRef = useRef(0);

  /**
   * The announce inputs, read through a ref so `emitTyping` and the debounce keep a
   * stable identity for the composer's lifetime: a debounce rebuilt mid-flight silently
   * drops the call it had pending.
   */
  const emitParams = { cid, announceTyping };
  const emitParamsRef = useRef(emitParams);
  emitParamsRef.current = emitParams;

  const emitTyping = useCallback((activityType: TypingActivityType) => {
    // Peers only need a stop while they are actually showing us as typing.
    if (activityType === "STOP_TYPING" && !isTypingRef.current) return;
    // A repeat start only refreshes the receiver's TTL, so it is throttled, never
    // dropped outright. A stop always goes out at once.
    const now = Date.now();
    if (
      activityType === "START_TYPING" &&
      isTypingRef.current &&
      now - lastStartAtRef.current < TYPING_REANNOUNCE_MS
    ) {
      return;
    }

    const params = emitParamsRef.current;
    const typing = activityType === "START_TYPING";
    isTypingRef.current = typing;
    if (typing) lastStartAtRef.current = now;
    // Best-effort, like the indicator it feeds: a lost announcement is healed by the
    // next keystroke or the receiver's TTL, so a failure is not surfaced.
    params.announceTyping({ params: { cid: params.cid }, body: { typing } });
  }, []);

  // Debounces the resolved ACTIVITY, not the value — a trailing flush then announces
  // the last intent (type-then-delete inside one window sends only the stop).
  const invokeTyping = useMemo(
    () => debounce(emitTyping, debounceDelay),
    [emitTyping, debounceDelay],
  );

  useEffect(() => {
    const activity = resolveTypingTransition(lastValueRef.current, value);
    // Sync even while disabled, so enabling later does not announce text that was
    // already sitting in the box.
    lastValueRef.current = value;
    if (!enabled || !activity) return;
    invokeTyping(activity);
  }, [enabled, value, invokeTyping]);

  useEffect(() => {
    if (!enabled) return;
    return () => {
      invokeTyping.cancel();
    };
  }, [enabled, invokeTyping]);

  const stopTyping = useCallback(() => {
    if (!enabled) return;
    invokeTyping.cancel();
    emitTyping("STOP_TYPING");
  }, [enabled, invokeTyping, emitTyping]);

  /**
   * Unmount-only stop, so leaving a channel mid-keystroke clears peers straight away
   * instead of waiting out their TTL. It reads a ref rather than living in the effect
   * above so it fires on real unmount only, never on a dependency change.
   *
   * Best-effort by nature: a hard tab close runs no React cleanup, which is why the
   * receiver (`ChannelTyping`) keeps its own TTL as the real backstop.
   */
  const unmountStopRef = useRef<() => void>(() => {});
  unmountStopRef.current = stopTyping;

  useEffect(() => () => unmountStopRef.current(), []);

  return { stopTyping };
}
